'use client';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Tooltip } from '@pickleballinc/react-ui';
import { DialogTitle, DialogHeader, DialogDescription } from '@pickleballinc/ui/components/ui/dialog';
import { Panel, PanelContent, PanelHeader, PanelBody, PanelClose } from '@pickleballinc/ui/components/ui/panel';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/atoms';
import { DatetimePickerForm } from '@/components/molecules/DatetimePickerForm';
import SelectForm from '@/components/molecules/SelectForm';
import { MatchCompletedEnum } from '@/domain/constants/constants';
import { SystemLocationsCourts } from '@/entities/SystemLocationsCourts';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useOrganizationStore } from '@/store/organization-store';
import { useTeamLeagueStore } from '@/store/team-league-store';

interface CourtAndTimeModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	courts: SystemLocationsCourts[];
	match: {
		matchUuid: string;
		courtUuid?: string;
		matchPlannedStart?: string;
		matchAssignedToCourt?: string;
	};
	moduleUuid: string;
	locationUuid: string;
	matchupUuid: string;
}

const matchTimeAndCourtFormSchema = z.object({
	matchUuid: z.string(),
	courtUuid: z.string().nullish(),
	matchPlannedStart: z.string().nullish(),
	date_assigned_to_court: z.string().nullish(),
	matchStatus: z.number().nullish(),
	matchCompletedType: z.nativeEnum(MatchCompletedEnum).nullish()
});

type MatchTimeAndCourtFormType = z.infer<typeof matchTimeAndCourtFormSchema>;

export const CourtAndTimeModal = ({ open, onOpenChange, courts, match, matchupUuid }: CourtAndTimeModalProps) => {
	const store = useOrganizationStore();
	const tlTimeZone = store.teamLeagueTimeZone;
	const queryClient = useQueryClient();
	const { labels } = useTeamLeagueStore();

	const form = useForm<MatchTimeAndCourtFormType>({
		resolver: zodResolver(matchTimeAndCourtFormSchema),
		values: {
			matchUuid: match.matchUuid,
			courtUuid: match.courtUuid || undefined,
			matchPlannedStart: match.matchPlannedStart
		}
	});

	const { mutate: updateCourtMutation, isPending: isUpdatingCourt } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Successfully updated DreamBreaker');
			// Update the match data with the new court UUID and planned start date from the form
			const newCourtUuid = form.getValues('courtUuid');
			const newPlannedStart = form.getValues('matchPlannedStart');
			if (newCourtUuid) {
				match.courtUuid = newCourtUuid;
			}
			if (newPlannedStart) {
				match.matchPlannedStart = newPlannedStart;
			}
			onOpenChange(false);
		}
	});

	const handleSave = form.handleSubmit((data: MatchTimeAndCourtFormType) => {
		try {
			updateCourtMutation(
				{
					matchId: match.matchUuid,
					updateData: {
						plannedStartDate: data.matchPlannedStart || '',
						courtUuid: data.courtUuid || '',
						dateAssignedToCourt: match.courtUuid === data.courtUuid ? match.matchAssignedToCourt : new Date().toISOString()
					}
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: matchKeys.matches(matchupUuid)
						});
					}
				}
			);
		} catch (error) {
			toast.error('Something went wrong and we were not able to save the changes.');
		}
	});

	const isDisabled =
		!form.formState.isDirty ||
		form.formState.isSubmitting ||
		isUpdatingCourt ||
		(match.courtUuid === form.watch('courtUuid') && match.matchPlannedStart === form.watch('matchPlannedStart'));

	return (
		<FormProvider {...form}>
			<Panel open={open} onOpenChange={onOpenChange}>
				<PanelContent className="flex w-full flex-col md:w-96 xl:w-100 2xl:w-120" aria-describedby="court-assignment-description">
					<PanelHeader className="flex flex-col px-6 pb-2 pt-4 border-b border-gray-200">
						<div className="flex items-center justify-between">
							<DialogHeader>
								<DialogTitle asChild>
									<h2 className="text-2xl font-semibold text-gray-900">Assign a court</h2>
								</DialogTitle>
							</DialogHeader>
							<PanelClose asChild>
								<button
									className="size-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
									aria-label="Close dialog"
								>
									<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
								</button>
							</PanelClose>
						</div>
						<DialogDescription id="court-assignment-description" className="text-base font-normal text-gray-600 text-start">
							Court assigning for the ${labels.tieBreakerMatchTitle}.
						</DialogDescription>
					</PanelHeader>

					<PanelBody className="flex-1 px-6 pt-4 pb-8">
						<div className="flex h-full flex-col">
							<div className="flex-1 space-y-4">
								<div className="flex flex-col gap-2">
									<label className="text-sm font-medium text-gray-700">Date</label>
									<DatetimePickerForm
										name="matchPlannedStart"
										className="w-full border-gray-300 shadow-sm rounded-md"
										placeholder="Select date and time"
										ianaTzIdentifier={tlTimeZone?.ianaTzIdentifier}
									/>
								</div>

								<SelectForm
									name="courtUuid"
									label="Court"
									options={courts.map((court) => ({ label: court.title, value: court.uuid })) ?? []}
									sendAsNumber={false}
									labelClassName="text-sm font-medium !text-gray-700"
								/>
							</div>

							<div className="flex flex-row gap-4">
								<Button
									onClick={() => onOpenChange(false)}
									className="!w-full justify-center border border-gray-300 !bg-white text-base font-semibold !text-gray-700 shadow-xs transition-all duration-200 hover:shadow-sm"
									disabled={isUpdatingCourt}
								>
									Cancel
								</Button>
								{!form.formState.isDirty ? (
									<Tooltip placement="top">
										<Tooltip.Trigger asChild>
											<Button
												onClick={handleSave}
												className={clsx('w-full border text-base font-semibold shadow-xs transition-all duration-200', {
													'cursor-not-allowed border border-gray-200 !bg-gray-100 !text-gray-400': isDisabled,
													'border-gray-200 !bg-orange-500 text-white hover:!bg-orange-700 hover:shadow-sm': !isDisabled
												})}
												disabled={isDisabled}
											>
												Save
											</Button>
										</Tooltip.Trigger>
										<Tooltip.Content className="z-[100]">
											<p>To enable saving, change the time or court first</p>
										</Tooltip.Content>
									</Tooltip>
								) : (
									<Button
										onClick={handleSave}
										className={clsx('w-full border text-base font-semibold shadow-xs transition-all duration-200', {
											'cursor-not-allowed border border-gray-200 !bg-gray-100 !text-gray-400': isDisabled,
											'border-gray-200 !bg-orange-500 text-white hover:!bg-orange-700 hover:shadow-sm': !isDisabled
										})}
										disabled={isDisabled}
									>
										Save
									</Button>
								)}
							</div>
						</div>
					</PanelBody>
				</PanelContent>
			</Panel>
		</FormProvider>
	);
};
